/**
 * One Custom MCP connection's screen: its tools, its access, and (when each
 * person signs in) its sign-in setup, as tabs. Routed at
 * `/connections/:integrationId/tools`, with `?tab=access` or `?tab=sign-in`.
 * Independent lists: which of the server's advertised tools the assistant may
 * use, and which groups' members may use them. Nothing is approved and nobody
 * has access until an admin says so here. The Sign-in tab shows what Test
 * found about the login system, takes the client an admin registered there
 * when the login system cannot register us, and lists who has signed in.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  Select,
  Skeleton,
  StatusIndicator,
  Table,
} from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { useMe } from '../../hooks/useMe.js';
import { formatDate } from '../../lib/format.js';
import type {
  McpToolDescriptor,
  TenantIntegrationSummary,
  TenantIntegrationTestResult,
} from '../../lib/types.js';
import { useGroupsListLogic } from '../groups/useGroupsLogic.js';
import { connectionStatus, loginProviderName, TestResultBanner } from './shared.js';
import { useIntegrationsListLogic } from './useIntegrationsListLogic.js';
import { useMcpConnectionLogic, type McpOAuthClientInput } from './useMcpConnectionLogic.js';
import { useMcpSignInsLogic, type McpSignInsLogic } from './useMcpSignInsLogic.js';

type Tab = 'tools' | 'access' | 'sign-in';

const tabFromParam = (value: string | null, signIn: boolean): Tab =>
  value === 'access' ? 'access' : value === 'sign-in' && signIn ? 'sign-in' : 'tools';

/** The client the admin typed; null means nothing changed on this visit. */
interface ClientDraft {
  clientId: string;
  clientSecret: string;
}

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && [...a].sort().every((value, i) => value === [...b].sort()[i]);

const hostOf = (url: string | undefined): string => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname === '/' ? '' : parsed.pathname}`;
  } catch {
    return url;
  }
};

/** The server's own hint about a tool, as quiet text; nothing when it gave none. */
const ToolHint = ({ tool }: { tool: McpToolDescriptor }) => {
  const hints = tool.annotations;
  if (!hints) return null;
  if (hints.readOnlyHint === true) {
    return <span className="text-ink-muted whitespace-nowrap">Read-only</span>;
  }
  if (hints.readOnlyHint === false || hints.destructiveHint === true) {
    return <span className="text-danger whitespace-nowrap">Writes</span>;
  }
  return null;
};

export const McpConnectionPage = () => {
  const navigate = useNavigate();
  const { integrationId } = useParams<{ integrationId: string }>();
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const tenantId = me.data?.tenant.id;
  const list = useIntegrationsListLogic(tenantId);
  const groups = useGroupsListLogic(tenantId);
  const mcp = useMcpConnectionLogic(tenantId);

  const row = useMemo(
    () => list.data?.integrations.find((item) => item.id === integrationId),
    [list.data, integrationId],
  );
  const signIn = row?.key === 'custom_mcp' && row.mcpAuthMode === 'user_oauth';
  const signIns = useMcpSignInsLogic(tenantId, row?.id, signIn);

  // The tab lives in the URL so a link can land on Access or Sign-in. Tools is
  // the default and stays out of the URL.
  const tab = tabFromParam(params.get('tab'), signIn);
  const setTab = (next: Tab) => {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'tools') out.delete('tab');
        else out.set('tab', next);
        return out;
      },
      { replace: true },
    );
  };

  // Drafts hold only what the admin has changed on this visit; null means
  // "whatever the row says", so a refresh after Test never clobbers edits.
  const [draftTools, setDraftTools] = useState<string[] | null>(null);
  const [draftGroups, setDraftGroups] = useState<string[] | null>(null);
  const [draftClient, setDraftClient] = useState<ClientDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const backLink = <Breadcrumbs trail={[{ label: 'All connections', to: '/connections' }]} />;

  if (me.isLoading || list.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-4 h-5 w-40" />
        <Skeleton className="mb-6 h-8 w-56" />
        <Skeleton className="h-64 w-full" />
      </AppShell>
    );
  }

  if (!row || row.key !== 'custom_mcp') {
    return (
      <AppShell>
        {backLink}
        <Banner variant="danger" className="mt-4">
          <Banner.Body>
            <Banner.Title>This is not a Custom MCP connection.</Banner.Title>
            <Banner.Description>
              Only a Custom MCP server has tools to approve and groups to grant.
            </Banner.Description>
          </Banner.Body>
        </Banner>
      </AppShell>
    );
  }

  return (
    <McpConnectionDetail
      row={row}
      groups={groups.data ?? []}
      status={connectionStatus(row)}
      backLink={backLink}
      tab={tab}
      onTab={setTab}
      draftTools={draftTools}
      draftGroups={draftGroups}
      draftClient={draftClient}
      onDraftTools={setDraftTools}
      onDraftGroups={setDraftGroups}
      onDraftClient={setDraftClient}
      signIns={signIns}
      testing={list.testingId === row.id}
      onTest={() => void list.test(row.id)}
      testResult={list.testResult?.integrationId === row.id ? list.testResult.result : null}
      onEdit={() => void navigate(`/connections/${row.id}/edit`)}
      saving={mcp.isSaving}
      saveError={saveError ?? mcp.error?.message ?? null}
      onSave={async (tools, groupIds, client) => {
        setSaveError(null);
        try {
          if (tools) await mcp.saveTools(row.id, tools);
          if (groupIds) await mcp.saveGroups(row.id, groupIds);
          if (client) await mcp.saveClient(row.id, client);
          setDraftTools(null);
          setDraftGroups(null);
          setDraftClient(null);
        } catch (err) {
          setSaveError(err instanceof Error ? err.message : 'Could not save.');
        }
      }}
    />
  );
};

interface DetailProps {
  row: TenantIntegrationSummary;
  groups: ReadonlyArray<{ id: string; name: string; memberCount: number }>;
  status: ReturnType<typeof connectionStatus>;
  backLink: React.ReactNode;
  tab: Tab;
  onTab: (tab: Tab) => void;
  draftTools: string[] | null;
  draftGroups: string[] | null;
  draftClient: ClientDraft | null;
  onDraftTools: (tools: string[] | null) => void;
  onDraftGroups: (groupIds: string[] | null) => void;
  onDraftClient: (client: ClientDraft | null) => void;
  signIns: McpSignInsLogic;
  testing: boolean;
  onTest: () => void;
  testResult: TenantIntegrationTestResult | null;
  onEdit: () => void;
  saving: boolean;
  saveError: string | null;
  /** Each part is passed only when it changed; null leaves it alone. */
  onSave: (
    tools: string[] | null,
    groupIds: string[] | null,
    client: McpOAuthClientInput | null,
  ) => Promise<void>;
}

const TAB_BUTTON = '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors';
const TAB_ACTIVE = 'border-primary text-primary';
const TAB_INACTIVE = 'border-transparent text-ink-muted hover:text-ink';

/** Marks a tab that holds unsaved changes, so the other tab's edits are not forgotten. */
const UnsavedDot = () => (
  <span
    aria-label="unsaved changes"
    className="bg-primary ml-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle"
  />
);

const McpConnectionDetail = ({
  row,
  groups,
  status,
  backLink,
  tab,
  onTab,
  draftTools,
  draftGroups,
  draftClient,
  onDraftTools,
  onDraftGroups,
  onDraftClient,
  signIns,
  testing,
  onTest,
  testResult,
  onEdit,
  saving,
  saveError,
  onSave,
}: DetailProps) => {
  const title = row.name || 'Custom MCP server';
  const signIn = row.mcpAuthMode === 'user_oauth';
  const oauth = row.mcpOAuth;
  const storedClientId = oauth?.clientId ?? '';
  const clientId = draftClient?.clientId ?? storedClientId;
  const clientSecret = draftClient?.clientSecret ?? '';
  const clientChanged =
    signIn && draftClient !== null && (clientId !== storedClientId || clientSecret !== '');
  const clientValid = !clientChanged || clientId.trim().length > 0;
  const advertised = row.mcpTools ?? [];
  const approved = draftTools ?? row.mcpApprovedTools;
  const allowed = draftGroups ?? row.mcpAllowedGroupIds;
  const advertisedNames = new Set(advertised.map((tool) => tool.name));
  // Approved earlier, no longer offered by the server. Shown so the admin
  // clears them deliberately; never dropped behind their back.
  const missing = approved.filter((name) => !advertisedNames.has(name));
  const toolsChanged = !sameSet(approved, row.mcpApprovedTools);
  const groupsChanged = !sameSet(allowed, row.mcpAllowedGroupIds);
  const dirty = toolsChanged || groupsChanged || clientChanged;
  const disconnected = row.isConnected === false;
  const groupById = new Map(groups.map((group) => [group.id, group]));
  const addable = groups.filter((group) => !allowed.includes(group.id));

  const toggleTool = (name: string, on: boolean) => {
    const next = on ? [...new Set([...approved, name])] : approved.filter((n) => n !== name);
    onDraftTools(next);
  };

  return (
    <AppShell fill>
      {backLink}
      <div className="mt-3 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h2 className="text-ink text-2xl font-bold">{title}</h2>
            <Badge variant="neutral">Custom MCP</Badge>
            {signIn && <Badge variant="neutral">Each person signs in</Badge>}
            <StatusIndicator tone={status.tone}>{status.label}</StatusIndicator>
          </div>
          <p className="text-ink-subtle mt-1.5 font-mono text-xs">
            {hostOf(row.config.url)}
            {' · '}
            {row.lastTestedAt ? `Tested ${formatDate(row.lastTestedAt)}` : 'Never tested'}
            {row.mcpTools ? ` · ${String(advertised.length)} tools advertised` : ''}
            {signIn && !signIns.isLoading
              ? ` · ${String(signIns.users.length)} ${signIns.users.length === 1 ? 'person' : 'people'} signed in`
              : ''}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant={row.mcpTools || (signIn && oauth) ? 'secondary' : 'primary'}
            onClick={onTest}
            disabled={testing || disconnected}
          >
            {testing ? 'Testing…' : !signIn && row.mcpTools ? 'Refresh tools' : 'Test connection'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Edit
          </Button>
        </div>
      </div>

      {disconnected && (
        <Banner variant="warning" className="mt-4">
          <Banner.Body>
            <Banner.Title>Disconnected.</Banner.Title>
            <Banner.Description>
              Reconnect it on the Connections page to use its tools again. Approvals and groups are
              kept.
            </Banner.Description>
          </Banner.Body>
        </Banner>
      )}

      <div className="mt-4">
        <TestResultBanner testResult={testResult ? { label: title, result: testResult } : null} />
      </div>

      <div className="border-outline mt-5 flex gap-1 border-b" role="tablist" aria-label={title}>
        <button
          type="button"
          id="mcp-tools-tab"
          role="tab"
          aria-selected={tab === 'tools'}
          aria-controls="mcp-tools-panel"
          onClick={() => onTab('tools')}
          className={`${TAB_BUTTON} ${tab === 'tools' ? TAB_ACTIVE : TAB_INACTIVE}`}
        >
          Tools
          {toolsChanged && <UnsavedDot />}
        </button>
        <button
          type="button"
          id="mcp-access-tab"
          role="tab"
          aria-selected={tab === 'access'}
          aria-controls="mcp-access-panel"
          onClick={() => onTab('access')}
          className={`${TAB_BUTTON} ${tab === 'access' ? TAB_ACTIVE : TAB_INACTIVE}`}
        >
          Access
          {groupsChanged && <UnsavedDot />}
        </button>
        {signIn && (
          <button
            type="button"
            id="mcp-sign-in-tab"
            role="tab"
            aria-selected={tab === 'sign-in'}
            aria-controls="mcp-sign-in-panel"
            onClick={() => onTab('sign-in')}
            className={`${TAB_BUTTON} ${tab === 'sign-in' ? TAB_ACTIVE : TAB_INACTIVE}`}
          >
            Sign-in
            {clientChanged && <UnsavedDot />}
          </button>
        )}
      </div>

      {/* Only the tab's content scrolls; the title, tabs and Save stay put. */}
      <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
        {tab === 'tools' ? (
          <section id="mcp-tools-panel" role="tabpanel" aria-labelledby="mcp-tools-tab">
            {!row.mcpTools ? (
              <Card padding="lg" className="border-dashed">
                <div className="flex flex-col items-center gap-2 py-4 text-center">
                  <span className="text-ink text-[15px] font-semibold">No tool list yet</span>
                  <span className="text-ink-muted max-w-[52ch] text-sm">
                    {signIn
                      ? 'The list fills when the first person signs in. Once you are signed in yourself, Test also refreshes it.'
                      : 'Test the connection to load what the server offers.'}
                  </span>
                  {!signIn && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="mt-1"
                      onClick={onTest}
                      disabled={testing || disconnected}
                    >
                      Test connection
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              <>
                <p className="text-ink-muted mb-2 text-sm">
                  Ticked tools are the ones the assistant may use.
                </p>
                <Card padding="none">
                  <Table.Root className="w-full text-sm">
                    <Table.Header sticky>
                      <Table.Row>
                        <Table.HeaderCell className="w-10" />
                        <Table.HeaderCell>Tool</Table.HeaderCell>
                        <Table.HeaderCell>Description</Table.HeaderCell>
                        <Table.HeaderCell className="w-32">Hint</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {advertised.map((tool) => {
                        const id = `tool-${tool.name}`;
                        return (
                          <Table.Row key={tool.name}>
                            <Table.Cell>
                              <input
                                id={id}
                                type="checkbox"
                                className="accent-primary"
                                checked={approved.includes(tool.name)}
                                disabled={saving}
                                onChange={(e) => toggleTool(tool.name, e.target.checked)}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <label htmlFor={id} className="text-ink font-mono text-[13px]">
                                {tool.name}
                              </label>
                            </Table.Cell>
                            <Table.Cell className="text-ink-muted">
                              {tool.description ?? tool.title ?? ''}
                            </Table.Cell>
                            <Table.Cell>
                              <ToolHint tool={tool} />
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                      {missing.map((name) => (
                        <Table.Row key={`missing-${name}`} className="bg-surface">
                          <Table.Cell>
                            <input
                              type="checkbox"
                              className="accent-primary"
                              aria-label={`Clear ${name}`}
                              checked
                              disabled={saving}
                              onChange={() => toggleTool(name, false)}
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <span className="text-ink-muted font-mono text-[13px] line-through">
                              {name}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="text-ink-muted">
                            No longer advertised by the server. Untick to clear.
                          </Table.Cell>
                          <Table.Cell>
                            <span className="text-warning whitespace-nowrap">Missing</span>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Card>
                <p className="text-ink-subtle mt-2 text-xs">
                  Hints come from the server. Writes means the tool can change data there.
                </p>
              </>
            )}
          </section>
        ) : tab === 'access' ? (
          <section id="mcp-access-panel" role="tabpanel" aria-labelledby="mcp-access-tab">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
              <p className="text-ink-muted text-sm">
                {allowed.length === 0
                  ? 'Nobody has access yet.'
                  : 'People in these groups can use the approved tools.'}
              </p>
              <Select
                id="mcp-add-group"
                options={addable.map((group) => ({ value: group.id, label: group.name }))}
                value={[]}
                onChange={(value) => {
                  const id = value[0];
                  if (id) onDraftGroups([...allowed, id]);
                }}
                placeholder={
                  addable.length === 0 ? 'Every group already has access' : 'Add a group…'
                }
                disabled={saving || addable.length === 0}
                className="min-w-[280px]"
                aria-label="Add a group"
              />
            </div>
            {allowed.length === 0 ? (
              <Card padding="lg" className="border-dashed">
                <div className="flex flex-col items-center gap-1 py-4 text-center">
                  <span className="text-ink text-[15px] font-semibold">No groups yet</span>
                  <span className="text-ink-muted max-w-[48ch] text-sm">
                    Add a group above. Its members get the approved tools once you save.
                  </span>
                </div>
              </Card>
            ) : (
              <Card padding="none">
                <Table.Root className="w-full text-sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Group</Table.HeaderCell>
                      <Table.HeaderCell>Members</Table.HeaderCell>
                      <Table.HeaderCell className="w-28" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {allowed.map((groupId) => {
                      const group = groupById.get(groupId);
                      const name = group?.name ?? 'Unknown group';
                      return (
                        <Table.Row key={groupId}>
                          <Table.Cell className="text-ink font-medium">{name}</Table.Cell>
                          <Table.Cell className="text-ink-muted">
                            {group
                              ? `${String(group.memberCount)} ${group.memberCount === 1 ? 'member' : 'members'}`
                              : 'Group no longer exists'}
                          </Table.Cell>
                          <Table.Cell>
                            <div className="flex justify-end">
                              <Button
                                size="sm"
                                variant="danger"
                                aria-label={`Remove ${name}`}
                                disabled={saving}
                                onClick={() =>
                                  onDraftGroups(allowed.filter((id) => id !== groupId))
                                }
                              >
                                Remove
                              </Button>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              </Card>
            )}
          </section>
        ) : (
          <SignInPanel
            row={row}
            signIns={signIns}
            clientId={clientId}
            clientSecret={clientSecret}
            onClient={(next) => onDraftClient(next)}
            saving={saving}
            testing={testing}
            onTest={onTest}
          />
        )}
      </div>

      {saveError && (
        <Banner variant="danger" className="mt-4">
          <Banner.Body>
            <Banner.Title>Could not save.</Banner.Title>
            <Banner.Description>{saveError}</Banner.Description>
          </Banner.Body>
        </Banner>
      )}

      <div className="mt-4 flex shrink-0 justify-end">
        <Button
          variant="primary"
          disabled={!dirty || saving || !clientValid}
          onClick={() =>
            void onSave(
              toolsChanged ? approved : null,
              groupsChanged ? allowed : null,
              clientChanged
                ? {
                    clientId: clientId.trim(),
                    ...(clientSecret !== '' ? { clientSecret } : {}),
                  }
                : null,
            )
          }
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </AppShell>
  );
};

interface SignInPanelProps {
  row: TenantIntegrationSummary;
  signIns: McpSignInsLogic;
  clientId: string;
  clientSecret: string;
  onClient: (next: ClientDraft) => void;
  saving: boolean;
  testing: boolean;
  onTest: () => void;
}

const FIELD_LABEL = 'text-ink-subtle text-xs font-semibold uppercase tracking-wide';
const FIELD_INPUT =
  'bg-surface border-outline-strong text-ink w-full rounded border px-3 py-2 text-sm disabled:opacity-50';

/**
 * What Test found about the login system, the client we hold there (or the
 * one-time registration the admin does when the login system cannot register
 * us), and who has signed in.
 */
const SignInPanel = ({
  row,
  signIns,
  clientId,
  clientSecret,
  onClient,
  saving,
  testing,
  onTest,
}: SignInPanelProps) => {
  const oauth = row.mcpOAuth;
  const [copied, setCopied] = useState(false);
  const provider = loginProviderName(oauth?.issuer ?? null);
  const issuerHost = (() => {
    if (!oauth) return '';
    try {
      const url = new URL(oauth.issuer);
      return `${url.host}${url.pathname === '/' ? '' : url.pathname}`;
    } catch {
      return oauth.issuer;
    }
  })();
  const copyRedirect = async () => {
    if (!oauth?.redirectUri) return;
    try {
      await navigator.clipboard.writeText(oauth.redirectUri);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section
      id="mcp-sign-in-panel"
      role="tabpanel"
      aria-labelledby="mcp-sign-in-tab"
      className="flex flex-col gap-6"
    >
      {!oauth ? (
        <Card padding="lg" className="border-dashed">
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <span className="text-ink text-[15px] font-semibold">Not tested yet</span>
            <span className="text-ink-muted max-w-[52ch] text-sm">
              Test the connection to find out how the server signs people in and whether anything
              else is needed.
            </span>
            <Button
              size="sm"
              variant="secondary"
              className="mt-1"
              onClick={onTest}
              disabled={testing || row.isConnected === false}
            >
              Test connection
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div>
            <div className="mb-2 flex flex-col gap-0.5">
              <span className={FIELD_LABEL}>Login system</span>
              <span className="text-ink-muted text-sm">
                What Test found when it asked the server how people sign in.
              </span>
            </div>
            <Card padding="md">
              <dl className="grid grid-cols-[180px_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
                <dt className="text-ink-muted">Signs in with</dt>
                <dd className="text-ink flex flex-wrap items-center gap-2.5">
                  <span>{provider}</span>
                  <span className="text-ink-subtle font-mono text-xs">{issuerHost}</span>
                </dd>
                <dt className="text-ink-muted">Platform registered there</dt>
                <dd className="flex flex-wrap items-center gap-2.5">
                  {oauth.registeredVia === 'dcr' ? (
                    <Badge variant="success">Registered automatically</Badge>
                  ) : oauth.clientId ? (
                    <Badge variant="success">Client supplied</Badge>
                  ) : (
                    <Badge variant="info">Needs a client</Badge>
                  )}
                  {oauth.registeredVia !== 'dcr' && (
                    <span className="text-ink-muted">
                      {provider} does not let apps register themselves, so this is done once by
                      hand.
                    </span>
                  )}
                </dd>
              </dl>
            </Card>
          </div>

          {oauth.registeredVia !== 'dcr' && (
            <div>
              <div className="mb-2 flex flex-col gap-0.5">
                <span className={FIELD_LABEL}>Register SC Platform with {provider}</span>
                <span className="text-ink-muted max-w-[82ch] text-sm leading-relaxed">
                  In your {provider} admin tools, add an app registration for SC Platform as a web
                  application with the redirect URL below, then paste what you are given here.
                  Nobody at SC needs to be involved.
                </span>
              </div>
              <Card padding="md" className="flex max-w-[840px] flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="mcp-redirect-uri" className={FIELD_LABEL}>
                    Redirect URL to register
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="mcp-redirect-uri"
                      readOnly
                      value={oauth.redirectUri ?? 'Not set on this platform yet'}
                      className={`${FIELD_INPUT} text-ink-muted font-mono text-xs`}
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!oauth.redirectUri}
                      onClick={() => void copyRedirect()}
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="mcp-client-id" className={FIELD_LABEL}>
                      Application (client) ID
                    </label>
                    <input
                      id="mcp-client-id"
                      value={clientId}
                      disabled={saving}
                      onChange={(e) => onClient({ clientId: e.target.value, clientSecret })}
                      placeholder="00000000-0000-0000-0000-000000000000"
                      className={`${FIELD_INPUT} font-mono text-[13px]`}
                      autoComplete="off"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="mcp-client-secret" className={FIELD_LABEL}>
                      Client secret{' '}
                      <span className="font-normal normal-case tracking-normal">
                        (if one was issued)
                      </span>
                    </label>
                    <input
                      id="mcp-client-secret"
                      type="password"
                      value={clientSecret}
                      disabled={saving}
                      onChange={(e) => onClient({ clientId, clientSecret: e.target.value })}
                      placeholder={
                        oauth.clientId
                          ? 'Leave blank to keep the current secret'
                          : 'Stored in Key Vault, never shown again'
                      }
                      className={FIELD_INPUT}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </Card>
            </div>
          )}

          <div>
            <div className="mb-2 flex flex-col gap-0.5">
              <span className={FIELD_LABEL}>Who is signed in</span>
              <span className="text-ink-muted text-sm">
                {signIns.users.length === 0
                  ? 'Nobody has signed in yet. The first sign-in also fills the tool list.'
                  : `${String(signIns.users.length)} ${signIns.users.length === 1 ? 'person' : 'people'}. Revoking removes that person's sign-in; they can sign in again any time. Sign-ins expire on their own after 90 days.`}
              </span>
            </div>
            {signIns.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : signIns.users.length > 0 ? (
              <Card padding="none">
                <Table.Root className="w-full text-sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Person</Table.HeaderCell>
                      <Table.HeaderCell>Email</Table.HeaderCell>
                      <Table.HeaderCell>Signed in</Table.HeaderCell>
                      <Table.HeaderCell>Last used</Table.HeaderCell>
                      <Table.HeaderCell className="w-28" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {signIns.users.map((user) => (
                      <Table.Row key={user.userId}>
                        <Table.Cell className="text-ink">{user.name ?? user.email}</Table.Cell>
                        <Table.Cell className="text-ink-muted">{user.email}</Table.Cell>
                        <Table.Cell className="text-ink-muted">
                          {formatDate(user.signedInAt)}
                        </Table.Cell>
                        <Table.Cell className="text-ink-muted">
                          {user.lastUsedAt ? formatDate(user.lastUsedAt) : 'Not yet'}
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={signIns.revokingId === user.userId}
                              onClick={() => void signIns.revoke(user.userId)}
                            >
                              {signIns.revokingId === user.userId ? 'Revoking…' : 'Revoke'}
                            </Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Card>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
};
